import { useMemo } from 'react'
import { isOneWayFlight } from 'lib/flights/flightUtils'
import { last, unique } from 'lib/array/arrayUtils'
import useOrderFlightDetails from 'hooks/Flights/useOrderFlightDetails'

export default function useFlightPurchaseDetails(order: App.Order) {
  const {
    status,
    flightItems,
  } = order

  const [firstFlight, fetchingFirst] = useOrderFlightDetails(order.id, order.flightItems[0].itemId)
  const [lastFlight, fetchingLast] = useOrderFlightDetails(order.id, last(order.flightItems).itemId)
  const fetching = fetchingFirst || fetchingLast

  const [departing, returning, travellers, isOneWayFare] = useMemo(() => {
    if (firstFlight && lastFlight) {
      const { departing, returning, legNumber, fareType } = firstFlight.journey
      return [
        departing,
        legNumber !== undefined ? lastFlight.journey.departing : returning,
        firstFlight.travellers,
        isOneWayFlight(fareType!),
      ]
    }
    return [undefined, undefined, [], undefined]
  }, [firstFlight, lastFlight])

  const pnrIds = flightItems.map(flightItem => flightItem.pnrId)
  const noOfAdults = useMemo(() => travellers.filter(({ type }) => type === 'adult').length, [travellers])
  const noOfChildren = useMemo(() => travellers.filter(({ type }) => type === 'child').length, [travellers])
  const noOfInfants = useMemo(() => travellers.filter(({ type }) => type === 'infant').length, [travellers])
  const departingFlight = departing?.flights[0]
  const arrivalFlight = isOneWayFare ? departing?.flights[departing?.flights.length - 1] : returning?.flights[0]
  const arrivalFlightLabel = isOneWayFare ? arrivalFlight?.arrivalDisplayNames.primary + ', ' + arrivalFlight?.arrivalCountry : arrivalFlight?.departingDisplayNames.primary + ', ' + arrivalFlight?.departingCountry
  const flightTypeLabel = isOneWayFare ? 'Oneway Flights' : 'Return Flights'
  const carrierLogos = useMemo(() => unique([firstFlight?.journey.validatingCarrierLogo, lastFlight?.journey.validatingCarrierLogo]), [firstFlight, lastFlight])
  const flightsName = departingFlight && arrivalFlight && `${departingFlight.departingDisplayNames.primary}, ${departingFlight.departingCountry} to ${arrivalFlightLabel}`
  const departingDate = departing?.flights[0].departingDate
  const arrivalDate = returning?.flights[0].arrivalDate ?? departingDate

  return {
    fetching,
    flightsName,
    departingDate,
    arrivalDate,
    noOfAdults,
    noOfChildren,
    noOfInfants,
    departingFlight,
    arrivalFlight,
    arrivalFlightLabel,
    flightTypeLabel,
    carrierLogos,
    pnrIds,
    status,
  }
}
